import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAlertVisibilityParams {
    autoClose?: boolean;
    duration?: number;
    animationDuration?: number;
    onDismiss?: () => void;
}

export function useAlertVisibility({
    autoClose = false,
    duration,
    animationDuration = 0,
    onDismiss,
}: UseAlertVisibilityParams) {
    const [isMounted, setIsMounted] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoDismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleDismiss = useCallback(() => {
        setIsVisible(false);

        if (dismissTimeoutRef.current) {
            clearTimeout(dismissTimeoutRef.current);
        }

        dismissTimeoutRef.current = setTimeout(() => {
            onDismiss?.();
        }, animationDuration);
    }, [animationDuration, onDismiss]);

    useEffect(() => {
        setIsMounted(true);

        const raf = requestAnimationFrame(() => {
            setIsVisible(true);
        });

        return () => cancelAnimationFrame(raf);
    }, []);

    useEffect(() => {
        if (!autoClose || !duration || duration <= 0) return;

        autoDismissTimeoutRef.current = setTimeout(() => {
            handleDismiss();
        }, duration);

        return () => {
            if (autoDismissTimeoutRef.current) {
                clearTimeout(autoDismissTimeoutRef.current);
            }
        };
    }, [autoClose, duration, handleDismiss]);

    useEffect(() => {
        return () => {
            if (dismissTimeoutRef.current) clearTimeout(dismissTimeoutRef.current);
            if (autoDismissTimeoutRef.current) clearTimeout(autoDismissTimeoutRef.current);
        };
    }, []);

    return {
        isMounted,
        isVisible,
        handleDismiss,
    };
}
